import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Search, Plus, Minus, Trash2, PauseCircle, Receipt, IndianRupee, Package, AlertTriangle, Clock, ShoppingCart, TrendingUp, Shield, QrCode, ScanLine } from 'lucide-react';
import { ReceiptDialog } from './dashboard/ReceiptDialog';
import { SubstitutesDialog } from './dashboard/SubstitutesDialog';
import { QRPaymentDialog } from './dashboard/QRPaymentDialog';
import { DrugAlertsDialog } from './dashboard/DrugAlertsDialog';
import { BillingForm } from './dashboard/BillingForm';
import { DashboardSidebar } from './dashboard/DashboardSidebar';
import { BarcodeScanner } from './BarcodeScanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, bgColor, iconColor, value, label }) {
  return (
    <div className="stat-mini">
      <div className={`stat-icon ${bgColor}`}><Icon className={`w-5 h-5 ${iconColor}`} /></div>
      <div><p className="stat-value">{value}</p><p className="stat-label">{label}</p></div>
    </div>
  );
}

function MedicineResult({ medicine, batch, onAdd, onShowSubstitutes }) {
  if (!batch || batch.quantity <= 0) {
    return (
      <div className="medicine-card out-of-stock">
        <div className="flex justify-between">
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-slate-500">{medicine.brand_name}</p>
              <Badge className="text-xs bg-red-100 text-red-600">Out of Stock</Badge>
            </div>
            <p className="text-xs text-slate-400">{medicine.salt_composition}</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="text-xs text-blue-600 border-blue-300"
            onClick={function(e) { e.stopPropagation(); onShowSubstitutes(medicine); }}
          >
            Find Substitutes
          </Button>
        </div>
      </div>
    );
  }
  const isScheduled = medicine.schedule_type && medicine.schedule_type !== 'none';
  return (
    <div className="medicine-card" onClick={onAdd}>
      <div className="flex justify-between">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium">{medicine.brand_name}</p>
            {isScheduled && (
              <Badge className="text-xs bg-red-100 text-red-800 border-red-300">
                <Shield className="w-3 h-3 mr-1" />{medicine.schedule_type}
              </Badge>
            )}
          </div>
          <p className="text-xs text-slate-500">{medicine.salt_composition}</p>
          <p className="text-xs text-slate-400">Batch: {batch.batch_id}</p>
        </div>
        <div className="text-right">
          <p className="font-bold text-mint-600">Rs.{medicine.mrp}</p>
          <p className="text-xs text-slate-500">Stock: {batch.quantity}</p>
        </div>
      </div>
    </div>
  );
}

function CartRow({ item, onQtyChange, onDiscountChange, onRemove }) {
  const amt = (item.mrp * item.quantity) * (1 - item.discount_percentage / 100);
  return (
    <tr className="cart-item-row">
      <td className="py-2">
        <p className="font-medium">{item.brand_name}</p>
        <p className="text-xs text-slate-500">{item.batch_id}</p>
      </td>
      <td className="py-2">
        <div className="flex items-center gap-1 justify-center">
          <Button variant="outline" size="icon" className="h-6 w-6" onClick={function() { onQtyChange(-1); }}><Minus className="w-3 h-3" /></Button>
          <span className="w-6 text-center">{item.quantity}</span>
          <Button variant="outline" size="icon" className="h-6 w-6" onClick={function() { onQtyChange(1); }}><Plus className="w-3 h-3" /></Button>
        </div>
      </td>
      <td className="py-2 text-right">Rs.{item.mrp}</td>
      <td className="py-2"><Input type="number" min="0" max="100" value={item.discount_percentage} onChange={function(e) { onDiscountChange(e.target.value); }} className="w-14 h-6 text-right text-xs" /></td>
      <td className="py-2 text-right font-medium">Rs.{amt.toFixed(2)}</td>
      <td className="py-2"><Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={onRemove}><Trash2 className="w-3 h-3" /></Button></td>
    </tr>
  );
}

function Dashboard() {
  const searchRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [medicines, setMedicines] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [parkedBills, setParkedBills] = useState([]);
  const [expiringMeds, setExpiringMeds] = useState([]);
  const [lowStockMeds, setLowStockMeds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [cart, setCart] = useState([]);
  const [patientType, setPatientType] = useState('walk-in');
  const [patientName, setPatientName] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [himsRxId, setHimsRxId] = useState('');
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastBill, setLastBill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [prescriberName, setPrescriberName] = useState('');
  const [prescriberAddress, setPrescriberAddress] = useState('');
  const [dpdpConsent, setDpdpConsent] = useState(false);
  const [consentForReminders, setConsentForReminders] = useState(false);
  const [showComplianceWarning, setShowComplianceWarning] = useState(false);
  const [showSubstitutes, setShowSubstitutes] = useState(false);
  const [substitutesData, setSubstitutesData] = useState(null);
  const [showQRPayment, setShowQRPayment] = useState(false);
  const [qrPaymentData, setQRPaymentData] = useState(null);
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  const [paymentChecking, setPaymentChecking] = useState(false);
  const [paymentConnected, setPaymentConnected] = useState(false);
  const [drugAlerts, setDrugAlerts] = useState([]);
  const [showDrugAlerts, setShowDrugAlerts] = useState(false);
  const [whatsAppEnabled, setWhatsAppEnabled] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const fetchData = useCallback(async function() {
    try {
      const [statsRes, medsRes, docsRes, custRes, parkedRes, expiringRes, lowStockRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/medicines`),
        axios.get(`${API}/doctors`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/parked-bills`),
        axios.get(`${API}/dashboard/expiring-soon`),
        axios.get(`${API}/dashboard/low-stock`)
      ]);
      setStats(statsRes.data);
      setMedicines(medsRes.data);
      setDoctors(docsRes.data);
      setCustomers(custRes.data);
      setParkedBills(parkedRes.data);
      setExpiringMeds(expiringRes.data);
      setLowStockMeds(lowStockRes.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    }
  }, []);

  useEffect(function() { fetchData(); }, [fetchData]);

  useEffect(function() {
    async function checkPaymentSettings() {
      try {
        const res = await axios.get(`${API}/payment-settings`);
        setPaymentConnected(res.data.onboarding_status === 'connected');
      } catch (e) { setPaymentConnected(false); }
    }
    checkPaymentSettings();
  }, []);

  useEffect(function() {
    async function checkWhatsAppSettings() {
      try {
        const res = await axios.get(`${API}/whatsapp-settings`);
        setWhatsAppEnabled(res.data.enable_invoice_sending && res.data.twilio_account_sid);
      } catch (e) { setWhatsAppEnabled(false); }
    }
    checkWhatsAppSettings();
  }, []);

  useEffect(function() {
    async function fetchInvoiceSettings() {
      try {
        const res = await axios.get(`${API}/invoice-settings`);
        setInvoiceSettings(res.data);
      } catch (e) { setInvoiceSettings(null); }
    }
    fetchInvoiceSettings();
  }, []);

  useEffect(function() {
    const himsData = localStorage.getItem('himsPrescription');
    if (himsData) {
      try {
        const prescription = JSON.parse(himsData);
        setHimsRxId(prescription.rx_id || '');
        setPatientName(prescription.patient_name || '');
        setPrescriberName(prescription.doctor_name || '');
        setPatientType('referred');
        toast.success('HIMS prescription loaded: ' + prescription.rx_id);
        localStorage.removeItem('himsPrescription');
      } catch (e) { /* ignore */ }
    }
  }, []);

  useEffect(function() {
    if (searchTerm.length >= 2) {
      const results = [];
      for (let i = 0; i < medicines.length && results.length < 6; i++) {
        const m = medicines[i];
        if (m.brand_name.toLowerCase().includes(searchTerm.toLowerCase()) || m.salt_composition.toLowerCase().includes(searchTerm.toLowerCase())) {
          results.push(m);
        }
      }
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, medicines]);

  function addToCart(medicine, batch) {
    const key = medicine.id + '-' + batch.batch_id;
    const existing = cart.find(function(c) { return c.cart_key === key; });
    if (existing) {
      if (existing.quantity >= batch.quantity) { toast.error('Insufficient stock'); return; }
      setCart(cart.map(function(c) { return c.cart_key === key ? { ...c, quantity: c.quantity + 1 } : c; }));
    } else {
      setCart([...cart, {
        cart_key: key, medicine_id: medicine.id, brand_name: medicine.brand_name,
        batch_id: batch.batch_id, quantity: 1, mrp: medicine.mrp,
        gst_percentage: medicine.gst_percentage, discount_percentage: 0,
        stock_quantity: batch.quantity, schedule_type: medicine.schedule_type || 'none',
        salt_composition: medicine.salt_composition || ''
      }]);
      checkDrugInteractions(medicine.id);
    }
    setSearchTerm('');
    setSearchResults([]);
  }

  async function checkDrugInteractions(newMedicineId) {
    const medicineIds = cart.map(function(c) { return c.medicine_id; });
    medicineIds.push(newMedicineId);
    if (medicineIds.length < 2) return;
    try {
      const res = await axios.post(`${API}/clinical/check-cart-interactions`, medicineIds);
      if (res.data.alerts && res.data.alerts.length > 0) {
        setDrugAlerts(res.data.alerts);
        setShowDrugAlerts(true);
        if (res.data.has_high_severity) {
          toast.error('High severity drug interaction detected!');
        } else {
          toast.warning('Drug interaction alert - please review');
        }
      }
    } catch (e) { /* silent */ }
  }

  function hasScheduledDrugs() {
    for (let i = 0; i < cart.length; i++) {
      if (cart[i].schedule_type === 'H1' || cart[i].schedule_type === 'X') return true;
    }
    return false;
  }

  async function findSubstitutes(medicine) {
    try {
      const res = await axios.get(`${API}/medicines/${medicine.id}/substitutes`);
      setSubstitutesData(res.data);
      setShowSubstitutes(true);
    } catch (e) { toast.error('Failed to find substitutes'); }
  }

  function addSubstituteToCart(substitute) {
    const batch = substitute.batches.find(function(b) { return b.quantity > 0; });
    if (batch) {
      addToCart(substitute, batch);
      setShowSubstitutes(false);
      toast.success(`Added ${substitute.brand_name} as substitute`);
    } else {
      toast.error('No stock available');
    }
  }

  async function handleBarcodeScan(scannedCode) {
    try {
      const res = await axios.get(`${API}/medicines/barcode/${encodeURIComponent(scannedCode)}`);
      if (res.data.found) {
        const med = res.data.medicine;
        const batch = med.batches.find(function(b) { return b.quantity > 0; });
        if (batch) {
          addToCart(med, batch);
          toast.success('Scanned: ' + med.brand_name + ' (matched by ' + res.data.match_type + ')');
        } else {
          toast.warning(med.brand_name + ' found but out of stock');
          setSearchTerm(med.brand_name);
        }
      } else {
        toast.error('No medicine found for code: ' + scannedCode);
        setSearchTerm(scannedCode);
      }
    } catch (e) {
      toast.error('Scan lookup failed');
      setSearchTerm(scannedCode);
    }
  }

  function buildBillPayload(pmMethod) {
    const doc = doctors.find(function(d) { return d.id === selectedDoctor; });
    const cust = customers.find(function(c) { return c.id === selectedCustomer; });
    return {
      items: cart.map(function(c) {
        return {
          medicine_id: c.medicine_id, brand_name: c.brand_name, batch_id: c.batch_id,
          quantity: c.quantity, mrp: c.mrp, gst_percentage: c.gst_percentage,
          discount_percentage: c.discount_percentage, schedule_type: c.schedule_type || 'none'
        };
      }),
      patient_type: patientType,
      patient_name: cust ? cust.name : patientName,
      customer_id: selectedCustomer || null,
      doctor_id: selectedDoctor || null,
      doctor_name: doc ? doc.name : '',
      payment_method: pmMethod,
      notes: '',
      hims_rx_id: himsRxId || null,
      prescriber_name: prescriberName,
      prescriber_address: prescriberAddress,
      dpdp_consent: dpdpConsent,
      consent_for_refill_reminders: consentForReminders
    };
  }

  function validateCompliance() {
    if (hasScheduledDrugs() && (!prescriberName.trim() || !prescriberAddress.trim())) {
      setShowComplianceWarning(true);
      toast.error('COMPLIANCE LOCK: Prescriber Name and Address required for Schedule H1/X drugs');
      return false;
    }
    return true;
  }

  async function initiateDigitalPayment() {
    if (cart.length === 0) { toast.error('Empty cart'); return; }
    if (!validateCompliance()) return;
    setLoading(true);
    try {
      const billRes = await axios.post(`${API}/bills`, buildBillPayload('digital'));
      const paymentRes = await axios.post(`${API}/payments/create-order`, {
        bill_id: billRes.data.id, amount: billRes.data.grand_total
      });
      setQRPaymentData({ ...paymentRes.data, bill: billRes.data });
      setShowQRPayment(true);
      clearBill();
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create payment'); }
    setLoading(false);
  }

  async function checkPaymentStatus() {
    if (!qrPaymentData) return;
    setPaymentChecking(true);
    try {
      const res = await axios.get(`${API}/payments/status/${qrPaymentData.bill.id}`);
      if (res.data.status === 'paid') {
        toast.success('Payment Successful!');
        setShowQRPayment(false);
        setQRPaymentData(null);
        fetchData();
      } else {
        toast.info('Payment still pending. Ask customer to complete payment.');
      }
    } catch (e) { toast.error('Failed to check payment status'); }
    setPaymentChecking(false);
  }

  function updateQty(key, delta) {
    setCart(cart.map(function(c) {
      if (c.cart_key !== key) return c;
      return { ...c, quantity: Math.max(1, Math.min(c.stock_quantity, c.quantity + delta)) };
    }));
  }

  function updateDisc(key, val) {
    const disc = Math.min(100, Math.max(0, parseFloat(val) || 0));
    setCart(cart.map(function(c) { return c.cart_key === key ? { ...c, discount_percentage: disc } : c; }));
  }

  function removeItem(key) {
    setCart(cart.filter(function(c) { return c.cart_key !== key; }));
  }

  function getTotals() {
    let sub = 0, gst = 0, disc = 0;
    for (let i = 0; i < cart.length; i++) {
      const c = cart[i];
      const total = c.mrp * c.quantity;
      const d = total * (c.discount_percentage / 100);
      const g = (total - d) * (c.gst_percentage / 100);
      sub += total; disc += d; gst += g;
    }
    return { sub: sub.toFixed(2), gst: gst.toFixed(2), disc: disc.toFixed(2), grand: (sub - disc + gst).toFixed(2) };
  }

  async function parkBill() {
    if (cart.length === 0) { toast.error('Empty cart'); return; }
    const doc = doctors.find(function(d) { return d.id === selectedDoctor; });
    await axios.post(`${API}/parked-bills`, { items: cart.map(function(c) { return { medicine_id: c.medicine_id, brand_name: c.brand_name, batch_id: c.batch_id, quantity: c.quantity, mrp: c.mrp, gst_percentage: c.gst_percentage, discount_percentage: c.discount_percentage }; }), patient_type: patientType, patient_name: patientName, doctor_id: selectedDoctor || null, doctor_name: doc ? doc.name : '', notes: '' });
    toast.success('Parked'); clearBill(); fetchData();
  }

  async function resumeBill(bill) {
    setCart(bill.items.map(function(it, i) { return { ...it, cart_key: it.medicine_id + '-' + it.batch_id + '-' + i, stock_quantity: it.quantity + 10 }; }));
    setPatientType(bill.patient_type); setPatientName(bill.patient_name); setSelectedDoctor(bill.doctor_id || '');
    await axios.delete(`${API}/parked-bills/${bill.id}`); fetchData();
  }

  async function checkout() {
    if (cart.length === 0) { toast.error('Empty cart'); return; }
    if (!validateCompliance()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/bills`, buildBillPayload(paymentMethod));
      setLastBill(res.data); setShowReceipt(true); toast.success('Done!'); clearBill(); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  }

  function clearBill() {
    setCart([]); setPatientType('walk-in'); setPatientName(''); setSelectedCustomer('');
    setSelectedDoctor(''); setPaymentMethod('cash'); setHimsRxId('');
    setPrescriberName(''); setPrescriberAddress('');
    setDpdpConsent(false); setConsentForReminders(false); setShowComplianceWarning(false);
  }

  const totals = getTotals();

  // Build search results
  const searchElems = [];
  for (let i = 0; i < searchResults.length; i++) {
    const med = searchResults[i];
    const batches = med.batches || [];
    let hasStock = false;
    for (let j = 0; j < batches.length; j++) {
      if (batches[j].quantity > 0) {
        hasStock = true;
        const batch = batches[j];
        searchElems.push(
          <MedicineResult key={med.id + '-' + batch.batch_id} medicine={med} batch={batch}
            onAdd={function() { addToCart(med, batch); }} onShowSubstitutes={findSubstitutes} />
        );
      }
    }
    if (!hasStock) {
      searchElems.push(
        <MedicineResult key={med.id + '-oos'} medicine={med} batch={null}
          onAdd={function() {}} onShowSubstitutes={findSubstitutes} />
      );
    }
  }

  // Build cart rows
  const cartRows = [];
  for (let i = 0; i < cart.length; i++) {
    const item = cart[i];
    cartRows.push(<CartRow key={item.cart_key} item={item} onQtyChange={function(d) { updateQty(item.cart_key, d); }} onDiscountChange={function(v) { updateDisc(item.cart_key, v); }} onRemove={function() { removeItem(item.cart_key); }} />);
  }

  return (
    <div className="p-4">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
        <StatCard icon={IndianRupee} bgColor="bg-mint-100" iconColor="text-mint-600" value={'Rs.' + (stats?.today_sales || 0)} label="Today" />
        <StatCard icon={TrendingUp} bgColor="bg-navy-100" iconColor="text-navy-600" value={'Rs.' + (stats?.month_sales || 0)} label="Month" />
        <StatCard icon={ShoppingCart} bgColor="bg-blue-100" iconColor="text-blue-600" value={stats?.today_transactions || 0} label="Bills" />
        <StatCard icon={Package} bgColor="bg-purple-100" iconColor="text-purple-600" value={stats?.total_medicines || 0} label="Products" />
        <StatCard icon={AlertTriangle} bgColor="bg-amber-100" iconColor="text-amber-600" value={stats?.low_stock_count || 0} label="Low Stock" />
        <StatCard icon={Clock} bgColor="bg-red-100" iconColor="text-red-600" value={stats?.near_expiry_count || 0} label="Expiring" />
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-7 space-y-4">
          {/* Search */}
          <Card><CardContent className="p-3">
            <div className="flex gap-2">
              <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" /><Input ref={searchRef} data-testid="medicine-search" placeholder="Search by brand, salt, or batch..." className="pl-10 text-base" value={searchTerm} onChange={function(e) { setSearchTerm(e.target.value); }} /></div>
              <Button variant="outline" className="border-mint-300 text-mint-700 hover:bg-mint-50 shrink-0" onClick={function() { setShowScanner(true); }} data-testid="pos-scan-btn">
                <ScanLine className="w-4 h-4 mr-1" />Scan
              </Button>
            </div>
            {searchElems.length > 0 && <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">{searchElems}</div>}
          </CardContent></Card>

          {/* Cart */}
          <Card>
            <CardHeader className="py-2 px-4 bg-navy-900 rounded-t-lg"><CardTitle className="text-white text-sm">Bill ({cart.length})</CardTitle></CardHeader>
            <CardContent className="p-3">
              {cart.length === 0
                ? <div className="p-6 text-center text-slate-400"><ShoppingCart className="w-10 h-10 mx-auto mb-2 opacity-50" /><p className="text-sm">Cart empty</p></div>
                : <ScrollArea className="h-48"><table className="w-full text-sm"><thead><tr className="text-xs text-slate-500 border-b"><th className="text-left py-1">Item</th><th className="w-24 text-center py-1">Qty</th><th className="w-16 text-right py-1">MRP</th><th className="w-16 text-right py-1">Disc%</th><th className="w-20 text-right py-1">Amt</th><th className="w-8"></th></tr></thead><tbody>{cartRows}</tbody></table></ScrollArea>}
            </CardContent>
          </Card>

          {/* Billing Form */}
          <Card><CardContent className="p-3">
            <BillingForm
              customers={customers} doctors={doctors}
              selectedCustomer={selectedCustomer} setSelectedCustomer={setSelectedCustomer}
              patientName={patientName} setPatientName={setPatientName}
              patientType={patientType} setPatientType={setPatientType}
              selectedDoctor={selectedDoctor} setSelectedDoctor={setSelectedDoctor}
              paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
              himsRxId={himsRxId} setHimsRxId={setHimsRxId}
              hasScheduledDrugs={hasScheduledDrugs()}
              prescriberName={prescriberName} setPrescriberName={setPrescriberName}
              prescriberAddress={prescriberAddress} setPrescriberAddress={setPrescriberAddress}
              showComplianceWarning={showComplianceWarning}
              dpdpConsent={dpdpConsent} setDpdpConsent={setDpdpConsent}
              consentForReminders={consentForReminders} setConsentForReminders={setConsentForReminders}
            />
          </CardContent></Card>

          {/* Totals & Actions */}
          <Card><CardContent className="p-3">
            <div className="flex items-center justify-between gap-4 mb-3">
              <div className="grid grid-cols-4 gap-2 text-sm flex-1">
                <div>Sub: Rs.{totals.sub}</div>
                <div className="text-mint-600">-Rs.{totals.disc}</div>
                <div>+Rs.{totals.gst}</div>
                <div className="text-lg font-bold">Rs.{totals.grand}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button className="flex-1 bg-mint-600 hover:bg-mint-700" onClick={checkout} disabled={cart.length === 0 || loading} data-testid="checkout-btn"><Receipt className="w-4 h-4 mr-2" />{loading ? '...' : 'Cash'}</Button>
              {paymentConnected && (
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={initiateDigitalPayment} disabled={cart.length === 0 || loading} data-testid="digital-pay-btn">
                  <QrCode className="w-4 h-4 mr-2" />{loading ? '...' : 'Digital Pay'}
                </Button>
              )}
              <Button variant="outline" onClick={parkBill} disabled={cart.length === 0}><PauseCircle className="w-4 h-4 mr-1" />Park</Button>
            </div>
          </CardContent></Card>
        </div>

        {/* Sidebar */}
        <DashboardSidebar
          parkedBills={parkedBills}
          expiringMeds={expiringMeds}
          lowStockMeds={lowStockMeds}
          stats={stats}
          onResumeBill={resumeBill}
        />
      </div>

      {/* Dialogs */}
      <ReceiptDialog
        open={showReceipt}
        onOpenChange={setShowReceipt}
        lastBill={lastBill}
        invoiceSettings={invoiceSettings}
        whatsAppEnabled={whatsAppEnabled}
      />
      <SubstitutesDialog
        open={showSubstitutes}
        onOpenChange={setShowSubstitutes}
        substitutesData={substitutesData}
        onAddSubstitute={addSubstituteToCart}
      />
      <QRPaymentDialog
        open={showQRPayment}
        onOpenChange={function(open) { if (!open) { setShowQRPayment(false); setQRPaymentData(null); } else { setShowQRPayment(true); } }}
        qrPaymentData={qrPaymentData}
        onCheckPayment={checkPaymentStatus}
        paymentChecking={paymentChecking}
      />
      <DrugAlertsDialog
        open={showDrugAlerts}
        onOpenChange={setShowDrugAlerts}
        drugAlerts={drugAlerts}
        onRemoveLastItem={function() {
          if (cart.length > 0) setCart(cart.slice(0, -1));
          setShowDrugAlerts(false);
          setDrugAlerts([]);
        }}
      />
      <BarcodeScanner
        open={showScanner}
        onOpenChange={setShowScanner}
        onScanResult={handleBarcodeScan}
      />
    </div>
  );
}

export default Dashboard;
